import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  X,
  Presentation,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Video,
  Link2,
  Unlink,
  ExternalLink,
  ListChecks,
  RefreshCw,
  CheckCircle2,
  Circle,
  Users,
} from 'lucide-react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import BBALoader from '../../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../../components/crm/reportCards';
import MeetingSummaryContent from '../../components/MeetingSummaryContent';
import BreakthroughSessionBackground, {
  getBreakthroughTheme,
} from '../../components/portal/BreakthroughSessionBackground';
import {
  GLASS_PALETTE,
  GLASS_TEXT,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassAccentButton,
  GlassSearch,
  GlassEmpty,
  GlassDonut,
  SlidingPills,
} from '../../components/glass/GlassUi';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { db } from '../../firebase';
import { getInternalUsers } from '../../services/userService';
import { getCrmContactDisplayPhotoUrl } from '../../services/firestoreService';
import { triggerNewTaskNotification } from '../../services/notificationTriggerService';
import { parseFathomSummarySections } from '../../utils/fathomSummaryParser';
import {
  toJsDate,
  actionItemText,
  fathomRecordingUrl,
  recordingStartDate,
  scoreRecordingForReport,
  findRecordingForReport,
  loadInternalRecordingsBetween,
  buildFathomLink,
  loadMeetingSummary,
  matchInternalUserByEmail,
  buildFathomTaskKeys,
  loadTasksForRecording,
  findTaskForActionItem,
  createInternalTaskFromActionItem,
} from '../../utils/meetingReportFathom';

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

const DAY = 24 * 60 * 60 * 1000;
// Glass surfaces: PANEL = section tile inside the report modal, TILE = item tile nested in a panel
const PANEL = GLASS_INSET;
const TILE = 'rounded-xl border border-black/[0.06] bg-white/60 dark:border-white/[0.07] dark:bg-white/[0.04]';
const TILE_HOVER = 'transition hover:-translate-y-0.5 hover:border-[#F80707]/30 hover:bg-white/80 dark:hover:border-[#F80707]/40 dark:hover:bg-white/[0.07]';
const MUTED = 'text-gray-500 dark:text-white/[0.45]';
// Report modal panel: the kit's modal look, sized for the wide slide layout
const MODAL_PANEL = 'relative flex max-h-[92vh] w-full max-w-[80rem] flex-col overflow-hidden rounded-[28px] border border-white/80 bg-[#FBFAF8]/95 text-gray-900 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#14110f]/95 dark:text-white';
const PICKER_OVERLAY = 'fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm';
const MODAL_DIVIDER = 'border-black/[0.06] dark:border-white/[0.06]';
const SELECT_FIELD = 'rounded-xl border border-black/10 bg-white/70 px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#F80707]/50 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:[&>option]:bg-[#14110f]';
const ACCENT_LINK = 'inline-flex items-center gap-2 rounded-full bg-[#F80707] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98]';
const SOURCE_COLORS = GLASS_PALETTE;
const CLIENT_PROGRAM_TONES = {
  Lite: 'blue',
  Plus: 'lavender',
  Pro: 'mint',
  Hourly: 'amber',
  '5 Hours': 'coral',
  '10 Hours': 'blue',
  'A La Carte': 'lavender',
};
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtLongDate = (d) =>
  d ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
const fmtDate = (d) => (d ? d.toLocaleDateString() : '');
const fmtShortDate = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const fmtTime = (d) => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

const departmentEmoji = (dept) => {
  const d = (dept || '').toLowerCase();
  if (d.includes('sales')) return '💰';
  if (d.includes('operation')) return '⚙️';
  if (d.includes('finance')) return '📊';
  if (d.includes('tech')) return '💻';
  if (d.includes('marketing')) return '📣';
  if (d.includes('hr') || d.includes('human')) return '👥';
  if (d.includes('onboard')) return '🚀';
  if (d.includes('design')) return '🎨';
  return '📋';
};

const initialsOf = (name) =>
  String(name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const Pill = ({ tone = 'gray', children, title }) => {
  // Glass soft-badge tones (same recipe as GlassBadge, without the dot since pills carry emojis)
  const tones = {
    gray: 'bg-black/[0.05] text-gray-600 dark:bg-white/[0.06] dark:text-white/60',
    green: 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]',
    mint: 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]',
    yellow: 'bg-[#FFB547]/20 text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]',
    red: 'bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E]',
    blue: 'bg-[#5BC0EB]/20 text-[#17698F] dark:bg-[#5BC0EB]/[0.15] dark:text-[#5BC0EB]',
    amber: 'bg-[#FFB547]/20 text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]',
    lavender: 'bg-[#B9A3F5]/25 text-[#5B43A8] dark:bg-[#B9A3F5]/[0.15] dark:text-[#B9A3F5]',
    coral: 'bg-[#FF7A68]/[0.15] text-[#C2412C] dark:bg-[#FF7A68]/[0.15] dark:text-[#FF9A8A]',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone] || tones.gray}`}
    >
      {children}
    </span>
  );
};

const Avatar = ({ src, name, initials, size = 'w-10 h-10', ring = '' }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || ''}
        className={`${size} rounded-full object-cover flex-shrink-0 ${ring}`}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className={`${size} rounded-full bg-[#E9E4DD] dark:bg-white/10 flex items-center justify-center text-xs font-semibold text-[#1C1916] dark:text-white/[0.85] flex-shrink-0 ${ring}`}
    >
      {initials || initialsOf(name)}
    </div>
  );
};

const Logo = ({ src, name, size = 'w-12 h-12' }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <div
      className={`${size} shrink-0 rounded-full overflow-hidden bg-white dark:bg-white/[0.06] border border-black/10 dark:border-white/10 flex items-center justify-center`}
    >
      {src && !broken ? (
        <img src={src} alt={name || ''} className="max-w-[88%] max-h-[88%] object-contain" onError={() => setBroken(true)} />
      ) : (
        <span className="text-sm font-semibold text-gray-500 dark:text-white/70">{initialsOf(name)}</span>
      )}
    </div>
  );
};

const Panel = ({ title, icon, children, className = '' }) => (
  <div className={`${PANEL} p-5 ${className}`}>
    <GlassCardHeader
      title={(
        <>
          {icon && (
            <span aria-hidden="true">
              {icon}
            </span>
          )}
          {title}
        </>
      )}
    />
    <div className="mt-4">{children}</div>
  </div>
);

const EmptyText = ({ children }) => (
  <GlassEmpty className="py-4">{children}</GlassEmpty>
);

const isV2 = (report) => Number(report?.reportVersion) >= 2;

/* ------------------------------------------------------------------ */
/* Overview                                                             */
/* ------------------------------------------------------------------ */

function ConferencesPanel({ conferences, reportDate }) {
  return (
    <Panel title={`Conferences (${conferences.length})`} icon="🎤">
      {conferences.length === 0 ? (
        <EmptyText>No upcoming conferences at the time of the meeting</EmptyText>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {conferences.slice(0, 6).map((c, idx) => {
            const start = toJsDate(c.startTime);
            const daysAway =
              Number.isFinite(c.daysAway) && c.daysAway !== null
                ? c.daysAway
                : start && reportDate
                ? Math.round((start - reportDate) / DAY)
                : null;
            const soon = daysAway !== null && daysAway <= 7;
            return (
              <div
                key={c.id || idx}
                className={`flex items-center gap-3 rounded-xl border p-3 ${
                  soon
                    ? 'border-[#FFB547]/50 bg-[#FFB547]/10 dark:border-[#FFB547]/40 dark:bg-[#FFB547]/[0.08]'
                    : TILE
                }`}
              >
                <div
                  className={`flex-shrink-0 w-16 rounded-lg py-2 text-center ${
                    soon ? 'bg-[#FFB547]/25 dark:bg-[#FFB547]/[0.15]' : 'bg-black/[0.04] dark:bg-white/[0.06]'
                  }`}
                >
                  <div className="text-2xl font-bold leading-none text-gray-900 dark:text-white">
                    {daysAway === null ? '—' : daysAway <= 0 ? '🎉' : daysAway}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/[0.45] mt-1">
                    {daysAway === null ? 'no date' : daysAway <= 0 ? 'that day' : daysAway === 1 ? 'day away' : 'days away'}
                  </div>
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-semibold text-gray-800 dark:text-white/90 break-words leading-snug">
                    {c.title || c.name || 'Conference'}
                  </div>
                  {start && (
                    <div className="text-xs text-gray-600 dark:text-white/70 mt-0.5">
                      📅 {c.dateLabel || fmtShortDate(start)} {c.timeLabel ? `· ${c.timeLabel}` : ''}
                    </div>
                  )}
                  {c.location && (
                    <div className="text-xs text-gray-500 dark:text-white/[0.45] mt-0.5 break-words">📍 {c.location}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function VisitGroupsPanel({ groups }) {
  const count = groups.reduce((n, g) => n + (g.companies || []).length, 0);
  return (
    <Panel title={`Client Visits (${count})`} icon="🚐">
      {groups.length === 0 ? (
        <EmptyText>No visits scheduled at the time of the meeting</EmptyText>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-center gap-2 mb-3">
                {(g.types || []).map((t) => (
                  <Pill key={t} tone={t === 'CQC' ? 'lavender' : 'blue'}>
                    {t}
                  </Pill>
                ))}
                <span className="text-sm font-semibold text-gray-700 dark:text-white/[0.85] text-center">{g.dateLabel}</span>
                {g.isWithin7Days && <span aria-hidden="true">⭐</span>}
              </div>
              <div className={`grid gap-4 ${(g.companies || []).length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {(g.companies || []).map((c, idx) => (
                  <div key={c.id || idx} className={`${TILE} flex flex-col items-center text-center p-3 gap-2`}>
                    <Logo src={c.logo} name={c.company} size="w-14 h-14" />
                    <span className="text-sm font-semibold text-gray-800 dark:text-white/90 break-words leading-tight w-full">
                      {c.company}
                    </span>
                    {(c.contactPhoto || c.contactName) && (
                      <div className="flex items-center gap-2 max-w-full">
                        <Avatar src={c.contactPhoto} name={c.contactName} size="w-8 h-8" />
                        {c.contactName && (
                          <span className="text-xs text-gray-600 dark:text-white/70 break-words leading-tight text-left">
                            {c.contactName}
                          </span>
                        )}
                      </div>
                    )}
                    {c.status && (
                      <Pill tone={c.status === 'Met with' ? 'blue' : 'green'}>{c.status}</Pill>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function BreakthroughRow({ sessions }) {
  return (
    <div>
      <GlassCardHeader className="mb-4" title="🎓 Next Breakthrough Sessions" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }, (_, idx) => {
          const session = sessions[idx];
          if (!session) {
            return (
              <div key={`empty-${idx}`} className={`${PANEL} p-3 flex flex-col gap-2 opacity-50`}>
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-full bg-black/10 dark:bg-white/10" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-400 dark:text-white/40">No session</div>
                    <div className="text-xs text-gray-400 dark:text-white/40">—</div>
                  </div>
                </div>
                <div className="text-sm text-gray-400 dark:text-white/40">No upcoming session</div>
              </div>
            );
          }
          const theme = getBreakthroughTheme(session.department);
          const date = toJsDate(session.date);
          const initials = session.presenterInitials || initialsOf(session.presenter || session.label || 'BS');
          return (
            <div
              key={session.id || idx}
              className="relative overflow-hidden rounded-2xl text-white shadow-sm"
              style={{ background: theme.gradient }}
            >
              <BreakthroughSessionBackground department={session.department} />
              <div className="relative z-10 p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {session.presenterPhoto ? (
                    <img
                      src={session.presenterPhoto}
                      alt={session.presenter || 'Presenter'}
                      className="h-10 w-10 rounded-full object-cover ring-2 ring-white/40"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-white font-semibold text-xs">
                      {initials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/70">{session.label}</div>
                    <div className="text-xs text-white/[0.85]">{fmtShortDate(date)}</div>
                  </div>
                  <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                    {session.department || 'Unassigned'}
                  </span>
                </div>
                {session.needPresenter ? (
                  <>
                    <div className="text-sm font-semibold text-white flex items-center gap-1">
                      <span>⚠️</span>
                      <span>Need Presenter</span>
                    </div>
                    <div className="text-xs text-white/[0.85]">
                      {fmtShortDate(date)} • {session.startTime || ''} {session.startTime && session.endTime ? '-' : ''}{' '}
                      {session.endTime || ''}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-white">{session.eventName}</div>
                    <div className="text-xs text-white/[0.85]">
                      {session.presenter ? `Presenter: ${session.presenter}` : 'No presenter yet'}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewSection({ report, reportDate }) {
  const v2 = isV2(report);
  const conferences = report.conferences || [];

  // Legacy reports stored a flat visit list; group it by date so both shapes render alike.
  const visitGroups = useMemo(() => {
    if (Array.isArray(report.clientVisitGroups)) return report.clientVisitGroups;
    const byDate = new Map();
    (report.clientVisits || []).forEach((v, idx) => {
      const d = toJsDate(v.visitDate || v.preferredDate);
      const key = d ? d.toDateString() : 'unknown';
      if (!byDate.has(key)) {
        byDate.set(key, { key, dateLabel: d ? fmtShortDate(d) : 'No date', types: [], companies: [] });
      }
      const g = byDate.get(key);
      if (v.visitType && !g.types.includes(v.visitType)) g.types.push(v.visitType);
      g.companies.push({
        id: v.id || idx,
        company: v.companyName || v.text || 'Visit',
        contactName: v.contactName || '',
        status: v.status === 'completed' ? 'Met with' : v.status === 'scheduled' ? 'Scheduled' : v.status || '',
      });
    });
    return Array.from(byDate.values());
  }, [report]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ConferencesPanel conferences={conferences} reportDate={reportDate} />
        <VisitGroupsPanel groups={visitGroups} />
      </div>
      {v2 ? (
        <BreakthroughRow sessions={report.breakthroughSessions || []} />
      ) : (
        report.departmentCounts && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['General', '⚙️', report.departmentCounts.general],
              ['Tech', '💻', report.departmentCounts.technology],
              ['Marketing', '📣', report.departmentCounts.marketing],
              ['HR', '👥', report.departmentCounts.hr],
            ].map(([name, emoji, count]) => (
              <div key={name} className={`${TILE} p-4 text-center`}>
                <div className="text-2xl" aria-hidden="true">
                  {emoji}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{count || 0}</div>
                <div className="text-xs text-gray-500 dark:text-white/[0.45]">{name} fulfillment requests</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Growth — Last 30 Days                                                */
/* ------------------------------------------------------------------ */

function SourcePie({ heading, emoji, slices }) {
  const { darkMode } = useTheme();
  const colored = (slices || []).map((s, i) => ({ ...s, color: SOURCE_COLORS[i % SOURCE_COLORS.length] }));
  const total = colored.reduce((sum, s) => sum + (s.value || 0), 0);
  return (
    <Panel title={heading} icon={emoji}>
      {total === 0 ? (
        <EmptyText>No lead source data in that 30-day window</EmptyText>
      ) : (
        <div className="flex items-center justify-center gap-8 flex-wrap py-2">
          <div
            className="w-44 h-44 flex-shrink-0"
            role="img"
            aria-label={`${heading} pie chart`}
          >
            <GlassDonut data={colored.filter((s) => s.value > 0)} dataKey="value" nameKey="name" inner={70} outer={80} darkMode={darkMode} unit="leads">
              <div className="text-2xl font-medium text-gray-900 tabular-nums dark:text-white">{total}</div>
              <div className={`text-xs ${MUTED}`}>total</div>
            </GlassDonut>
          </div>
          <div className="space-y-2 text-sm text-gray-700 dark:text-white/75">
            {colored.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="min-w-[140px]">{s.name}</span>
                <span className="font-semibold text-gray-900 tabular-nums dark:text-white">{s.value}</span>
                <span className={`tabular-nums ${MUTED}`}>({((s.value / total) * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function GrowthSection({ growth }) {
  if (!growth) {
    return (
      <div className={`${PANEL} p-8 text-center text-sm text-gray-500 dark:text-white/[0.45]`}>
        This report was saved by the previous Meeting Mode, before the Growth slide existed.
      </div>
    );
  }
  const website = growth.website || {};
  const avgSeconds = website.avgSeconds || 0;
  const stat = (emoji, label, value, cls) => (
    <div className={`${TILE} p-4 text-center`}>
      <div className="text-3xl leading-none" aria-hidden="true">
        {emoji}
      </div>
      <div className={`mt-2 text-4xl font-semibold tabular-nums ${cls}`}>{value}</div>
      <div className="mt-1 text-sm font-medium text-gray-700 dark:text-white/70">{label}</div>
      <div className="text-xs text-gray-500 dark:text-white/[0.45]">30 days before the meeting</div>
    </div>
  );
  const borderColors = {
    Facebook: 'border-l-[#5BC0EB]',
    Instagram: 'border-l-[#F28BA8]',
    TikTok: 'border-l-[#1C1916] dark:border-l-white/70',
    YouTube: 'border-l-[#F80707]',
    LinkedIn: 'border-l-[#93B4F5]',
  };
  const textColors = {
    Facebook: GLASS_TEXT.blue,
    Instagram: 'text-[#C2456B] dark:text-[#F28BA8]',
    TikTok: 'text-gray-800 dark:text-white/[0.85]',
    YouTube: GLASS_TEXT.red,
    LinkedIn: 'text-[#3F67C2] dark:text-[#93B4F5]',
  };
  const platformIcons = {
    Facebook: '/facebook.svg',
    Instagram: '/instagram.svg',
    TikTok: '/tiktok.svg',
    YouTube: '/Youtube.svg',
    LinkedIn: '/Linkedin.svg',
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stat('🌱', 'New leads', growth.newLeads || 0, GLASS_TEXT.mint)}
        {stat('🤝', 'New clients', growth.newClients || 0, GLASS_TEXT.blue)}
        {stat('👋', 'Lost clients', growth.lostClients || 0, GLASS_TEXT.red)}
      </div>
      <p className="-mt-6 text-xs text-center text-gray-500 dark:text-white/[0.45]">
        New clients are counted from their start date; lost clients from when the account was last updated.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SourcePie heading="Lead Source — New Leads" emoji="🌱" slices={growth.leadSourceSlices} />
        <SourcePie heading="Lead Source — New Clients" emoji="🤝" slices={growth.clientSourceSlices} />
      </div>
      <Panel title="Website & Organic Traffic" icon="🌐">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            ['👥 Total Visitors', (website.totalVisitors || 0).toLocaleString()],
            ['⏱️ Avg. Session Duration', avgSeconds < 60 ? `${Math.round(avgSeconds)}s` : `${(avgSeconds / 60).toFixed(1)}m`],
            ['↩️ Bounce Rate', `${(website.avgBounce || 0).toFixed(1)}%`],
          ].map(([label, value]) => (
            <div key={label} className={`${TILE} p-4 text-center`}>
              <div className="text-sm font-medium text-gray-700 dark:text-white/70">{label}</div>
              <div className="mt-1 text-3xl font-semibold tabular-nums text-gray-900 dark:text-white">{value}</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-white/[0.45]">30 days before the meeting</div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Social Media Performance" icon="📱">
        {growth.hasSocialData && (growth.social || []).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {growth.social.map(({ platform, followers, change }) => (
              <div
                key={platform}
                className={`${TILE} p-4 border-l-4 ${borderColors[platform] || 'border-l-black/20 dark:border-l-white/20'} relative`}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 mb-3 flex items-center justify-center">
                    <img
                      src={platformIcons[platform] || `/${String(platform).toLowerCase()}.svg`}
                      alt={`${platform} icon`}
                      className="w-14 h-14"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <h3 className="text-sm font-bold text-center text-gray-800 dark:text-white">{platform}</h3>
                  <div className={`text-lg font-bold mt-2 ${textColors[platform] || ''}`}>{(followers || 0).toLocaleString()}</div>
                  <div className="text-xs text-gray-500 dark:text-white/[0.45]">followers</div>
                  {change && (
                    <div
                      className={`flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full font-semibold text-sm ${
                        change.isIncrease
                          ? 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]'
                          : 'bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E]'
                      }`}
                    >
                      <span aria-hidden="true">{change.isIncrease ? '⬆️' : '⬇️'}</span>
                      <span>{(change.value || 0).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyText>No social media data available</EmptyText>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Clients                                                              */
/* ------------------------------------------------------------------ */

function ClientCard({ row, attention, monthLabel }) {
  const resetDate = toJsDate(row.resetDate);
  const lastEmail = toJsDate(row.lastEmailDate);
  const checkIn = row.checkIn;
  const renderCall = (c, i, upcoming) => {
    const d = toJsDate(c.date);
    return (
      <div key={i} className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar src={c.photo} name={c.label} initials={c.initials} size="w-9 h-9" />
          <span className="text-base text-gray-800 dark:text-white/90 truncate">{c.label}</span>
        </div>
        <span
          className={`text-sm font-medium whitespace-nowrap ${
            upcoming ? GLASS_TEXT.blue : 'text-gray-500 dark:text-white/[0.45]'
          }`}
        >
          {fmtDate(d)}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        attention
          ? 'border-[#FFB547]/50 bg-[#FFB547]/[0.08] dark:border-[#FFB547]/30 dark:bg-[#FFB547]/[0.05]'
          : TILE
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Logo src={row.logo} name={row.company} />
          <div className="min-w-0">
            <div className="text-lg font-semibold text-gray-800 dark:text-white/90 truncate">{row.company || 'Unknown'}</div>
            {row.stage && (
              <div className="mt-1">
                <Pill tone={CLIENT_PROGRAM_TONES[row.stage] || 'gray'}>{row.stage}</Pill>
              </div>
            )}
          </div>
        </div>
        {row.limit > 0 && (
          <div className={`${GLASS_INSET} flex-shrink-0 text-center px-4 py-2`}>
            <div className="text-4xl font-semibold tabular-nums leading-none text-gray-900 dark:text-white">
              {row.used}
              <span className="text-gray-400 dark:text-white/40">/</span>
              {row.limit}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/[0.45] mt-1">1:1 calls</div>
            {resetDate && (
              <div className="text-[10px] text-gray-500 dark:text-white/[0.45] mt-0.5 whitespace-nowrap">
                🔄 resets {fmtShortDate(resetDate)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <Pill>
          ⏳{' '}
          {row.monthsWithUs == null
            ? 'Start date unknown'
            : row.monthsWithUs === 0
            ? 'First month with us'
            : `${row.monthsWithUs} ${row.monthsWithUs === 1 ? 'month' : 'months'} with us`}
        </Pill>
        <Pill tone={!checkIn ? 'gray' : checkIn.responded ? 'green' : 'yellow'} title={checkIn?.comments || ''}>
          {!checkIn
            ? `📝 No ${monthLabel} check-in`
            : checkIn.responded
            ? `⭐ ${monthLabel} check-in: ${checkIn.rating != null ? `${checkIn.rating}/5` : checkIn.status}`
            : `⏰ ${monthLabel} check-in sent, no reply`}
        </Pill>
        {lastEmail && <Pill>✉️ Last email {fmtDate(lastEmail)}</Pill>}
      </div>

      {(row.members || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4">
          {row.members.map((m, i) => (
            <div key={m.id || i} className="flex flex-col items-center w-20 text-center">
              <div className="relative">
                <Avatar
                  src={m.photo}
                  name={m.name}
                  initials={m.initials}
                  size="w-14 h-14"
                  ring={m.pinned ? 'ring-2 ring-[#F80707]' : 'ring-1 ring-black/10 dark:ring-white/[0.15]'}
                />
                {m.pinned && (
                  <span className="absolute -top-1 -right-1 text-xs leading-none" aria-hidden="true">
                    📌
                  </span>
                )}
              </div>
              <span className="mt-1 text-xs text-gray-700 dark:text-white/[0.85] w-full break-words leading-tight" title={m.name}>
                {m.name}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-white/[0.85] mb-2">
          <span aria-hidden="true">📞</span> Met with · 30 days before the meeting
        </div>
        {(row.recentCalls || []).length === 0 ? (
          <div className={`text-sm ${GLASS_TEXT.amber}`}>No calls logged</div>
        ) : (
          <div className="space-y-2">{row.recentCalls.slice(0, 4).map((c, i) => renderCall(c, i, false))}</div>
        )}
      </div>

      {(row.upcomingCalls || []).length > 0 && (
        <div className="mt-4">
          <div className={`flex items-center gap-1.5 text-sm font-semibold mb-2 ${GLASS_TEXT.blue}`}>
            <span aria-hidden="true">📅</span> Upcoming booked
          </div>
          <div className="space-y-2">{row.upcomingCalls.slice(0, 4).map((c, i) => renderCall(c, i, true))}</div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-white/[0.45] uppercase tracking-wide mb-1">
          <span aria-hidden="true">🗺️</span> Next on roadmap
        </div>
        {(row.nextSteps || []).length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-white/[0.45]">No upcoming roadmap items</div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {row.nextSteps.map((s, i) => (
              <div
                key={i}
                className={`${GLASS_INSET} flex flex-col items-center text-center p-2`}
              >
                <span className="text-2xl leading-none">{departmentEmoji(s.department)}</span>
                <span className="mt-1 text-xs font-medium text-gray-800 dark:text-white/90 line-clamp-2">{s.title}</span>
                <span className="mt-0.5 text-[10px] text-gray-400 dark:text-white/40">{s.department}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientsSection({ report }) {
  if (!isV2(report)) {
    const accounts = (report.allAccounts || []).filter((a) => a && (a.type === 'Clients' || a.type === 'clients'));
    if (accounts.length === 0) return <EmptyText>No clients were captured in this report</EmptyText>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((account) => (
          <div key={account.id} className={`${TILE} p-5 flex items-center gap-4`}>
            <Avatar
              src={getCrmContactDisplayPhotoUrl(account)}
              name={`${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`}
              size="w-14 h-14"
            />
            <div className="flex-1 min-w-0">
              <div className="text-lg font-semibold truncate text-gray-800 dark:text-white/90">{account.company}</div>
              {account.stage && <Pill>{account.stage}</Pill>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const activity = report.clientActivity || { rows: [], totals: {} };
  const totals = activity.totals || {};
  const monthLabel = activity.monthLabel || 'this month';
  const rows = activity.rows || [];
  const groups = [
    { key: 'active', title: 'Recently Active', subtitle: 'Met in the 30 days before the meeting', match: (r) => (r.recentCalls || []).length > 0 },
    { key: 'attention', title: 'Needs Attention', subtitle: 'No 1:1 call in the 30 days before the meeting', match: (r) => (r.recentCalls || []).length === 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          ['📅 Allotted', totals.total || 0, GLASS_TEXT.strong, 'across all live cycles'],
          [`✅ Used in ${monthLabel}`, totals.used || 0, GLASS_TEXT.blue, 'calls logged that month'],
          ['🎟️ Still bookable', totals.remaining || 0, GLASS_TEXT.mint, 'left before each reset'],
        ].map(([label, value, cls, sub]) => (
          <div key={label} className={`${GLASS_INSET} p-4 text-center`}>
            <div className="text-sm font-medium text-gray-700 dark:text-white/70">{label}</div>
            <div className={`mt-1 text-3xl font-semibold tabular-nums ${cls}`}>{value}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-white/[0.45]">{sub}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-center text-gray-500 dark:text-white/[0.45]">
        Every client resets on their own date, so allotted ≠ used + bookable. Each card shows its own reset day.
      </p>
      {rows.length === 0 ? (
        <EmptyText>No active clients were captured in this report</EmptyText>
      ) : (
        groups.map((group) => {
          const groupRows = rows.filter(group.match);
          if (groupRows.length === 0) return null;
          return (
            <div key={group.key} className="space-y-3">
              <div className="flex items-center justify-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">{group.title}</h3>
                <Pill tone={group.key === 'attention' ? 'amber' : 'blue'}>{groupRows.length}</Pill>
              </div>
              <p className="text-xs text-gray-500 dark:text-white/[0.45] text-center">{group.subtitle}</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {groupRows.map((row) => (
                  <ClientCard key={row.id} row={row} attention={group.key === 'attention'} monthLabel={monthLabel} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Outstanding Tasks                                                    */
/* ------------------------------------------------------------------ */

function TasksSection({ report, reportDate }) {
  const users = report.internalUsers || [];
  const tasksByUser = report.tasksByUser || {};
  const dayStart = reportDate ? new Date(reportDate) : null;
  if (dayStart) dayStart.setHours(0, 0, 0, 0);

  if (users.length === 0) return <EmptyText>No team members were captured in this report</EmptyText>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {users.map((user) => {
        const tasks = [...(tasksByUser[user.id] || [])].sort((a, b) => {
          const da = toJsDate(a.dueDate);
          const dbb = toJsDate(b.dueDate);
          if (!da && !dbb) return 0;
          if (!da) return 1;
          if (!dbb) return -1;
          return da - dbb;
        });
        return (
          <div key={user.id} className={`${PANEL} flex flex-col`}>
            <div className={`p-4 border-b ${MODAL_DIVIDER} flex flex-col items-center`}>
              <Avatar src={user.photoURL} name={user.name} initials={user.initials} size="w-12 h-12" />
              <div className="mt-2 text-sm font-medium text-gray-800 dark:text-white/90 text-center truncate max-w-[160px]">
                {user.name || 'User'}
              </div>
              <div className="text-xs text-gray-500 dark:text-white/[0.45]">
                {tasks.length} open {tasks.length === 1 ? 'task' : 'tasks'}
              </div>
            </div>
            <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '360px' }}>
              {tasks.length === 0 ? (
                <div className="text-xs text-gray-500 dark:text-white/[0.45] text-center py-6">No tasks</div>
              ) : (
                tasks.map((task, idx) => {
                  const due = toJsDate(task.dueDate);
                  const overdue = !!(due && dayStart && due < dayStart);
                  return (
                    <div
                      key={task.id || idx}
                      className={`rounded-xl p-3 border ${
                        overdue
                          ? 'border-[#F80707]/30 bg-[#F80707]/[0.06] dark:border-[#F80707]/30 dark:bg-[#F80707]/10'
                          : TILE
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${
                          overdue ? GLASS_TEXT.red : 'text-gray-800 dark:text-white/90'
                        }`}
                      >
                        {task.title || 'Untitled task'}
                      </div>
                      <div className={`mt-1 text-xs ${overdue ? GLASS_TEXT.red : 'text-gray-500 dark:text-white/[0.45]'}`}>
                        {due ? `${overdue ? 'Was overdue · ' : ''}${fmtDate(due)}` : 'No date'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Additional Items                                                     */
/* ------------------------------------------------------------------ */

function AdditionalSection({ report }) {
  const list = (items, empty) =>
    items.length === 0 ? (
      <EmptyText>{empty}</EmptyText>
    ) : (
      <div className="space-y-3">
        {items.map((t, idx) => (
          <div key={idx} className={`${TILE} p-4`}>
            <p className="text-sm text-gray-700 dark:text-white/[0.85] break-words">{t}</p>
          </div>
        ))}
      </div>
    );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Panel title="Questions" icon="❓">
        {list(report.questions || [], 'No questions were raised')}
      </Panel>
      <Panel title="Comments" icon="💬">
        {list(report.comments || [], 'No comments were added')}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Recording & Action Items (Fathom)                                    */
/* ------------------------------------------------------------------ */

function RecordingSection({
  report,
  reportDate,
  summary,
  summaryLoading,
  autoLinking,
  internalUsers,
  recordingTasks,
  itemKeys,
  assigneeById,
  onPickAssignee,
  creating,
  onCreateTask,
  onCreateAll,
  onOpenPicker,
  onUnlink,
  onRetryAutoLink,
}) {
  const link = report.fathom;
  const recorder = link?.recorderUid ? internalUsers.find((u) => u.id === link.recorderUid) : null;
  const url = link?.recordingUrl || fathomRecordingUrl(summary);
  const start = toJsDate(link?.scheduledStartTime) || recordingStartDate(summary);
  const actionItems = (summary?.actionItems || []).filter((item) => actionItemText(item));
  const sections = useMemo(() => parseFathomSummarySections(summary?.summary || ''), [summary]);

  const rows = actionItems.map((item, idx) => {
    const keys = itemKeys[idx] || null;
    const task = findTaskForActionItem(item, keys, recordingTasks);
    const fathomEmail = typeof item === 'object' ? item.assigneeEmail || '' : '';
    const matched = matchInternalUserByEmail(fathomEmail, internalUsers);
    const assigneeId = assigneeById[idx] !== undefined ? assigneeById[idx] : matched?.id || '';
    return { item, idx, keys, task, fathomEmail, matched, assigneeId };
  });
  const pending = rows.filter((r) => !r.task && r.assigneeId);
  const done = rows.filter((r) => r.task).length;

  if (!link) {
    return (
      <div className={`${PANEL} p-8 text-center space-y-4`}>
        <img src="/fathomlogo.png" alt="Fathom" className="w-14 h-14 rounded-full object-contain mx-auto" />
        <div className="text-lg font-semibold text-gray-900 dark:text-white">No Fathom recording linked yet</div>
        <p className="text-sm text-gray-500 dark:text-white/[0.45] max-w-xl mx-auto">
          {autoLinking
            ? 'Looking for an internal recording around the time this report was saved…'
            : 'Reports link automatically once Fathom finishes processing the recording (usually a few minutes after the call). You can also pick the recording yourself.'}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onRetryAutoLink}
            disabled={autoLinking}
            className={GLASS_SECONDARY_BUTTON}
          >
            <RefreshCw className={`w-4 h-4 ${autoLinking ? 'animate-spin' : ''}`} />
            Check again
          </button>
          <GlassAccentButton onClick={onOpenPicker}>
            <Link2 className="w-4 h-4" />
            Pick a recording
          </GlassAccentButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recording header */}
      <div className={`${TILE} p-5`}>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <img src="/fathomlogo.png" alt="Fathom" className="w-14 h-14 rounded-full object-contain flex-shrink-0 mx-auto md:mx-0" />
          <div className="flex-1 min-w-0 text-center md:text-left">
            <div className="text-lg font-semibold text-gray-900 dark:text-white break-words">
              {link.meetingTitle || summary?.meetingTitle || 'Fathom recording'}
            </div>
            <div className="mt-1 flex flex-wrap items-center justify-center md:justify-start gap-2 text-xs">
              {start && <Pill>📅 {fmtLongDate(start)} · {fmtTime(start)}</Pill>}
              {recorder && <Pill>🎙️ Recorded by {recorder.name}</Pill>}
              <Pill tone={link.linkedBy === 'auto' ? 'green' : 'blue'}>
                {link.linkedBy === 'auto' ? '✨ Linked automatically' : '🔗 Linked manually'}
              </Pill>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={ACCENT_LINK}
              >
                <Video className="w-4 h-4" />
                Open recording
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              type="button"
              onClick={onOpenPicker}
              title="Link a different recording"
              className={GLASS_SECONDARY_BUTTON}
            >
              <Link2 className="w-4 h-4" />
              Change
            </button>
            <button
              type="button"
              onClick={onUnlink}
              title="Unlink this recording"
              className={GLASS_SECONDARY_BUTTON}
            >
              <Unlink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {summaryLoading ? (
        <div className="flex justify-center items-center py-8">
          <BBALoader size={32} />
          <span className={`ml-3 ${MUTED}`}>Loading the Fathom summary…</span>
        </div>
      ) : !summary ? (
        <div className={`${PANEL} p-6 text-center text-sm text-gray-500 dark:text-white/[0.45]`}>
          The linked Fathom summary could not be found anymore. Unlink it and pick the recording again.
        </div>
      ) : (
        <>
          {/* Action items */}
          <Panel
            title={`Action Items (${done}/${actionItems.length} assigned)`}
            icon="✅"
          >
            {actionItems.length === 0 ? (
              <EmptyText>Fathom found no action items in this recording</EmptyText>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-gray-500 dark:text-white/[0.45]">
                    Each action item becomes a CRM task for the chosen team member. Fathom's assignee is pre-selected
                    when their email matches an internal user.
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href="/crm/tasks"
                      className={GLASS_SECONDARY_BUTTON}
                    >
                      <ListChecks className="w-3.5 h-3.5" />
                      Open Tasks
                    </a>
                    <GlassAccentButton
                      onClick={onCreateAll}
                      disabled={pending.length === 0 || Object.values(creating).some(Boolean)}
                    >
                      <Users className="w-3.5 h-3.5" />
                      Assign all ({pending.length})
                    </GlassAccentButton>
                  </div>
                </div>
                <ul className="space-y-2">
                  {rows.map(({ item, idx, task, fathomEmail, matched, assigneeId }) => {
                    const text = actionItemText(item);
                    const assigneeName = typeof item === 'object' ? item.assigneeName : '';
                    const taskAssignee = task
                      ? internalUsers.find((u) => (task.assignees || []).includes(u.id))
                      : null;
                    const isCreating = !!creating[idx];
                    return (
                      <li key={idx} className={`${TILE} p-3 flex flex-col md:flex-row md:items-center gap-3`}>
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {task ? (
                            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${GLASS_TEXT.mint}`} />
                          ) : (
                            <Circle className="w-5 h-5 text-black/20 dark:text-white/20 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{text}</p>
                            <p className="text-xs text-gray-500 dark:text-white/[0.45] mt-0.5">
                              {assigneeName || fathomEmail
                                ? `Fathom assigned: ${assigneeName || ''}${fathomEmail ? ` (${fathomEmail})` : ''}`
                                : 'Fathom did not name an assignee'}
                              {typeof item === 'object' && item.completed ? ' · marked done in Fathom' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 md:justify-end flex-shrink-0">
                          {task ? (
                            <Pill tone="green" title={`Task ${task.id}`}>
                              ✅ Task created{taskAssignee ? ` · ${taskAssignee.name}` : ''}
                              {task.completed ? ' · completed' : ''}
                            </Pill>
                          ) : (
                            <>
                              <select
                                value={assigneeId}
                                onChange={(e) => onPickAssignee(idx, e.target.value)}
                                className={SELECT_FIELD}
                              >
                                <option value="">Assign to…</option>
                                {internalUsers.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.name || u.email || u.id}
                                    {matched && matched.id === u.id ? ' (Fathom match)' : ''}
                                  </option>
                                ))}
                              </select>
                              <GlassAccentButton
                                onClick={() => onCreateTask(idx)}
                                disabled={!assigneeId || isCreating}
                                className="whitespace-nowrap"
                              >
                                {isCreating ? 'Creating…' : 'Create task'}
                              </GlassAccentButton>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Panel>

          {/* Summary */}
          {summary.summary ? (
            sections.hasStructuredSections ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  ['Meeting Purpose', '🎯', sections.meetingPurpose],
                  ['Key Takeaways', '💡', sections.keyTakeaways],
                  ['Topics', '🗂️', sections.topics],
                  ['Next Steps', '➡️', sections.nextSteps],
                ]
                  .filter(([, , body]) => body && body.trim())
                  .map(([title, emoji, body]) => (
                    <Panel key={title} title={title} icon={emoji}>
                      <MeetingSummaryContent markdown={body} className="!bg-transparent !p-0" />
                    </Panel>
                  ))}
              </div>
            ) : (
              <Panel title="Fathom Summary" icon="📝">
                <MeetingSummaryContent markdown={summary.summary} className="!bg-transparent !p-0" />
              </Panel>
            )
          ) : (
            <div className={`${PANEL} p-6 text-center text-sm text-gray-500 dark:text-white/[0.45]`}>
              Fathom did not include a written summary for this recording.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RecordingPicker({ open, onClose, reportDate, internalUsers, onLink, excludeSummaryId }) {
  const [wide, setWide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !reportDate) return undefined;
    let active = true;
    const span = wide ? 21 * DAY : 3 * DAY;
    setLoading(true);
    setError('');
    loadInternalRecordingsBetween(db, new Date(reportDate.getTime() - span), new Date(reportDate.getTime() + span))
      .then((list) => {
        if (!active) return;
        setCandidates(list.filter((s) => s.id !== excludeSummaryId));
      })
      .catch((e) => {
        console.error('Failed to load Fathom recordings:', e);
        if (active) setError('Could not load recordings. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, wide, reportDate, excludeSummaryId]);

  if (!open) return null;

  const suggested = candidates
    .map((c) => ({ c, score: scoreRecordingForReport(c, reportDate) }))
    .filter((x) => x.score != null)
    .sort((a, b) => a.score - b.score)[0]?.c;

  return (
    <div className={PICKER_OVERLAY} onClick={onClose}>
      <div className={`${MODAL_PANEL} !max-w-3xl !max-h-[85vh]`} onClick={(e) => e.stopPropagation()}>
        <div className={`px-6 py-4 border-b ${MODAL_DIVIDER} flex items-center justify-center relative`}>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white text-center">Link a Fathom recording</h2>
          <button type="button" onClick={onClose} className="absolute right-4 rounded-full p-1 text-gray-400 transition hover:text-[#F80707] dark:text-white/40 dark:hover:text-[#FF4D40]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className={`px-6 py-3 border-b ${MODAL_DIVIDER} flex items-center justify-between gap-3 flex-wrap text-sm`}>
          <span className="text-gray-600 dark:text-white/70">
            Internal recordings within {wide ? '3 weeks' : '3 days'} of {fmtLongDate(reportDate)}
          </span>
          <label className="inline-flex items-center gap-2 text-gray-600 dark:text-white/70 cursor-pointer">
            <input type="checkbox" checked={wide} onChange={(e) => setWide(e.target.checked)} className="rounded accent-[#F80707]" />
            Search a wider window
          </label>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <BBALoader size={32} />
            </div>
          ) : error ? (
            <EmptyText>{error}</EmptyText>
          ) : candidates.length === 0 ? (
            <EmptyText>No internal Fathom recordings in that window.</EmptyText>
          ) : (
            candidates.map((c) => {
              const start = recordingStartDate(c);
              const recorder = internalUsers.find((u) => u.id === c.recorderUid);
              const items = (c.actionItems || []).length;
              const isSuggested = suggested && suggested.id === c.id;
              return (
                <div
                  key={c.id}
                  className={`p-3 flex items-center gap-3 ${
                    isSuggested
                      ? 'rounded-xl border border-[#7FD9B0]/60 bg-[#7FD9B0]/[0.12] dark:border-[#7FD9B0]/40 dark:bg-[#7FD9B0]/[0.08]'
                      : TILE
                  }`}
                >
                  <img src="/fathomlogo.png" alt="" className="w-9 h-9 rounded-full object-contain flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {c.meetingTitle || 'Untitled recording'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-white/[0.45] flex flex-wrap gap-x-3">
                      {start && (
                        <span>
                          📅 {fmtDate(start)} {fmtTime(start)}
                        </span>
                      )}
                      {recorder && <span>🎙️ {recorder.name}</span>}
                      <span>✅ {items} action {items === 1 ? 'item' : 'items'}</span>
                      {isSuggested && <span className={`font-medium ${GLASS_TEXT.mint}`}>Suggested</span>}
                    </div>
                  </div>
                  <GlassAccentButton onClick={() => onLink(c)}>
                    Link
                  </GlassAccentButton>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

const MEETINGS_PER_PAGE = 12;
const AUTO_LINK_LOOKBACK_MS = 14 * DAY;
const AUTO_LINK_MAX = 5;

const MeetingsReports = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [internalUsers, setInternalUsers] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);

  // Fathom state for the open report
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [recordingTasks, setRecordingTasks] = useState([]);
  const [itemKeys, setItemKeys] = useState([]);
  const [assigneeById, setAssigneeById] = useState({});
  const [creating, setCreating] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const autoLinkTried = useRef(new Set());

  const selectedReport = useMemo(() => reports.find((r) => r.id === selectedId) || null, [reports, selectedId]);
  const selectedDate = selectedReport ? toJsDate(selectedReport.createdAt) || toJsDate(selectedReport.createdAtISO) : null;
  const linkedSummaryId = selectedReport?.fathom?.summaryId || null;

  /* ---------- data ---------- */

  useEffect(() => {
    const q = query(collection(db, 'meetingReports'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setReports(
          snapshot.docs.map((d) => {
            const data = d.data();
            const date = toJsDate(data.createdAt) || toJsDate(data.createdAtISO);
            return { id: d.id, ...data, _date: date };
          })
        );
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching meetings:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    getInternalUsers()
      .then((users) => setInternalUsers(users || []))
      .catch((e) => {
        console.error('Error loading internal users:', e);
        setInternalUsers([]);
      });
  }, []);

  const userById = useMemo(() => Object.fromEntries(internalUsers.map((u) => [u.id, u])), [internalUsers]);

  const persistLink = useCallback(
    async (reportId, recording, linkedBy) => {
      await updateDoc(doc(db, 'meetingReports', reportId), {
        fathom: buildFathomLink(recording, { linkedBy }),
      });
    },
    []
  );

  const tryAutoLink = useCallback(
    async (report) => {
      const date = report?._date || toJsDate(report?.createdAt);
      if (!report?.id || !date) return null;
      autoLinkTried.current.add(report.id);
      try {
        const recording = await findRecordingForReport(db, date);
        if (recording) await persistLink(report.id, recording, 'auto');
        return recording;
      } catch (e) {
        console.error('Fathom auto-link failed for report', report.id, e);
        return null;
      }
    },
    [persistLink]
  );

  // Background sweep: link recent reports that don't have a recording yet. Bounded so a
  // page load never fans out into more than a handful of Firestore reads.
  useEffect(() => {
    if (loading || reports.length === 0) return undefined;
    let active = true;
    const cutoff = Date.now() - AUTO_LINK_LOOKBACK_MS;
    const targets = reports
      .filter((r) => !r.fathom && r._date && r._date.getTime() >= cutoff && !autoLinkTried.current.has(r.id))
      .slice(0, AUTO_LINK_MAX);
    if (targets.length === 0) return undefined;
    (async () => {
      for (const report of targets) {
        if (!active) return;
        await tryAutoLink(report);
      }
    })();
    return () => {
      active = false;
    };
  }, [loading, reports, tryAutoLink]);

  // The open report: load its linked summary + tasks, or try to link it once.
  useEffect(() => {
    if (!selectedReport) {
      setSummary(null);
      setRecordingTasks([]);
      setItemKeys([]);
      return undefined;
    }
    let active = true;
    setAssigneeById({});
    setCreating({});

    if (!linkedSummaryId) {
      setSummary(null);
      setRecordingTasks([]);
      setItemKeys([]);
      if (!autoLinkTried.current.has(selectedReport.id)) {
        setAutoLinking(true);
        tryAutoLink(selectedReport).finally(() => {
          if (active) setAutoLinking(false);
        });
      }
      return () => {
        active = false;
      };
    }

    setSummaryLoading(true);
    (async () => {
      try {
        const loaded = await loadMeetingSummary(db, linkedSummaryId);
        if (!active) return;
        setSummary(loaded);
        if (loaded) {
          const items = (loaded.actionItems || []).filter((item) => actionItemText(item));
          const [tasks, keys] = await Promise.all([
            loadTasksForRecording(db, loaded.recordingId),
            Promise.all(items.map((item) => buildFathomTaskKeys({ summary: loaded, item }))),
          ]);
          if (!active) return;
          setRecordingTasks(tasks);
          setItemKeys(keys);
        } else {
          setRecordingTasks([]);
          setItemKeys([]);
        }
      } catch (e) {
        console.error('Failed to load the linked Fathom summary:', e);
        if (active) {
          setSummary(null);
          setRecordingTasks([]);
          setItemKeys([]);
        }
      } finally {
        if (active) setSummaryLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedReport, linkedSummaryId, tryAutoLink]);

  useEffect(() => {
    if (selectedId) setCurrentSection(0);
  }, [selectedId]);

  /* ---------- handlers ---------- */

  const handleRetryAutoLink = async () => {
    if (!selectedReport) return;
    setAutoLinking(true);
    try {
      const recording = await tryAutoLink(selectedReport);
      if (!recording) {
        alert('No internal Fathom recording was found near the time of this meeting yet. If Fathom is still processing, check again in a few minutes — or pick the recording manually.');
      }
    } finally {
      setAutoLinking(false);
    }
  };

  const handleManualLink = async (recording) => {
    if (!selectedReport) return;
    try {
      await persistLink(selectedReport.id, recording, currentUser?.uid || 'manual');
      setPickerOpen(false);
    } catch (e) {
      console.error('Failed to link recording:', e);
      alert('Could not link that recording. Please try again.');
    }
  };

  const handleUnlink = async () => {
    if (!selectedReport?.fathom) return;
    if (!window.confirm('Unlink this Fathom recording from the report? Tasks already created stay in place.')) return;
    try {
      await updateDoc(doc(db, 'meetingReports', selectedReport.id), { fathom: deleteField() });
      autoLinkTried.current.add(selectedReport.id); // don't immediately re-link the same recording
      setSummary(null);
    } catch (e) {
      console.error('Failed to unlink recording:', e);
      alert('Could not unlink the recording. Please try again.');
    }
  };

  const createTaskAt = async (idx) => {
    if (!summary || !selectedReport) return false;
    const items = (summary.actionItems || []).filter((item) => actionItemText(item));
    const item = items[idx];
    if (!item) return false;
    const fathomEmail = typeof item === 'object' ? item.assigneeEmail || '' : '';
    const assigneeId =
      assigneeById[idx] !== undefined ? assigneeById[idx] : matchInternalUserByEmail(fathomEmail, internalUsers)?.id || '';
    const assignee = userById[assigneeId];
    if (!assignee) return false;

    setCreating((prev) => ({ ...prev, [idx]: true }));
    try {
      const result = await createInternalTaskFromActionItem(db, {
        summary,
        item,
        assignee,
        createdBy: currentUser?.uid || null,
        meetingReportId: selectedReport.id,
      });
      if (!result.existed) {
        try {
          await triggerNewTaskNotification({
            taskTitle: result.payload.title,
            description: result.payload.description,
            assignees: result.payload.assignees,
            dueDate: null,
            priority: result.payload.priority,
            createdBy: currentUser?.uid || null,
            accountId: null,
          });
        } catch (e) {
          console.warn('Task notification failed:', e);
        }
      }
      return true;
    } catch (e) {
      console.error('Failed to create task from action item:', e);
      alert(e.message || 'Could not create the task. Please try again.');
      return false;
    } finally {
      setCreating((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const refreshRecordingTasks = async () => {
    if (!summary) return;
    try {
      setRecordingTasks(await loadTasksForRecording(db, summary.recordingId));
    } catch (e) {
      console.error('Failed to refresh tasks for recording:', e);
    }
  };

  const handleCreateTask = async (idx) => {
    await createTaskAt(idx);
    await refreshRecordingTasks();
  };

  const handleCreateAll = async () => {
    if (!summary) return;
    const items = (summary.actionItems || []).filter((item) => actionItemText(item));
    for (let idx = 0; idx < items.length; idx++) {
      const keys = itemKeys[idx] || null;
      if (findTaskForActionItem(items[idx], keys, recordingTasks)) continue;
      // eslint-disable-next-line no-await-in-loop
      await createTaskAt(idx);
    }
    await refreshRecordingTasks();
  };

  const handleDeleteMeeting = async () => {
    if (!selectedReport?.id) return;
    if (!window.confirm('Are you sure you want to delete this meeting report? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'meetingReports', selectedReport.id));
      setSelectedId(null);
      setCurrentSection(0);
    } catch (error) {
      console.error('Error deleting meeting report:', error);
      alert('Failed to delete meeting report. Please try again.');
    }
  };

  /* ---------- derived ---------- */

  const creatorName = (report) => userById[report.createdBy]?.name || '';

  const filteredMeetings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const haystack = [
        r._date ? fmtLongDate(r._date) : '',
        r._date ? r._date.toLocaleDateString() : '',
        r._date ? MONTH_NAMES[r._date.getMonth()] : '',
        creatorName(r),
        r.fathom?.meetingTitle || '',
        r.fathom ? 'recording fathom' : 'no recording',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, searchQuery, userById]);

  useEffect(() => setCurrentPage(1), [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredMeetings.length / MEETINGS_PER_PAGE));
  const pageStart = (Math.min(currentPage, totalPages) - 1) * MEETINGS_PER_PAGE;
  const currentMeetings = filteredMeetings.slice(pageStart, pageStart + MEETINGS_PER_PAGE);

  const withRecording = reports.filter((r) => r.fathom).length;
  const actionItemTotal = reports.reduce((n, r) => n + (r.fathom?.actionItemCount || 0), 0);
  const latest = reports[0]?._date || null;

  const sections = useMemo(() => {
    if (!selectedReport) return [];
    const v2 = isV2(selectedReport);
    return [
      { key: 'overview', title: 'Overview', icon: '🧭' },
      ...(v2 ? [{ key: 'growth', title: 'Growth — Last 30 Days', icon: '📈' }] : []),
      { key: 'clients', title: 'Clients', icon: '🏢' },
      { key: 'tasks', title: 'Outstanding Tasks', icon: '✅' },
      { key: 'additional', title: 'Additional Items', icon: '📝' },
      { key: 'recording', title: 'Recording & Action Items', icon: '🎥' },
    ];
  }, [selectedReport]);

  const section = sections[Math.min(currentSection, Math.max(0, sections.length - 1))];

  /* ---------- render ---------- */

  const stats = [
    { icon: Calendar, value: reports.length, label: 'Meeting reports' },
    { icon: Video, value: withRecording, label: 'With recording', valueClassName: GLASS_TEXT.mint },
    { icon: ListChecks, value: actionItemTotal, label: 'Action items', valueClassName: GLASS_TEXT.blue },
    { icon: Presentation, value: latest ? fmtShortDate(latest) : '—', label: 'Last meeting' },
  ];

  const pageBtn = (disabled) => `h-9 rounded-full px-4 text-sm font-medium transition ${
    disabled
      ? 'cursor-not-allowed border border-black/[0.05] text-gray-400 dark:border-white/[0.05] dark:text-white/20'
      : 'border border-black/10 text-gray-700 hover:bg-black/[0.04] dark:border-white/10 dark:text-white/70 dark:hover:bg-white/[0.06]'
  }`;

  const sectionOptions = sections.map((s, i) => ({ value: i, label: `${s.icon} ${s.title}` }));

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        title={<>Meetings<br />Reports</>}
        stats={stats}
        controls={(
          <>
            <GlassSearch
              className="w-full sm:w-80"
              placeholder="Search by date, month, presenter, or recording title…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <GlassAccentButton onClick={() => navigate('/crm/meeting-mode')} className="whitespace-nowrap">
              <Presentation className="h-4 w-4" />
              Meeting Mode
            </GlassAccentButton>
          </>
        )}
      />

      {loading && (
        <div className="flex h-64 items-center justify-center">
          <BBALoader size={96} />
        </div>
      )}

      {!loading && filteredMeetings.length === 0 && (
        <PinnableCard id="company-meetings" title="Company Meetings" size="wide" placement="corner">
        <GlassCard className="p-6" delay={0}>
          <GlassEmpty className="flex-col">
            <Calendar className="h-12 w-12 text-gray-400 dark:text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No meetings found</h3>
            <p>
              {reports.length === 0 ? 'Run Meeting Mode and press Finish to save the first report.' : 'Try adjusting your search.'}
            </p>
          </GlassEmpty>
        </GlassCard>
        </PinnableCard>
      )}

      {!loading && filteredMeetings.length > 0 && (
        <PinnableCard id="company-meetings" title="Company Meetings" size="wide">
        <GlassCard className="p-5" delay={0}>
          <GlassCardHeader title="Company Meetings" />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {currentMeetings.map((report) => {
              const by = creatorName(report);
              const creator = userById[report.createdBy];
              return (
                <button
                  type="button"
                  key={report.id}
                  onClick={() => setSelectedId(report.id)}
                  className={`${GLASS_INSET} ${TILE_HOVER} p-5 text-center flex flex-col items-center gap-2`}
                >
                  <span className="text-3xl leading-none" aria-hidden="true">
                    📋
                  </span>
                  <div className="text-base font-medium text-gray-900 dark:text-white leading-snug">
                    {report._date ? fmtLongDate(report._date) : 'Unknown date'}
                  </div>
                  <div className={`text-xs flex items-center gap-1.5 ${MUTED}`}>
                    {report._date && <span>{fmtTime(report._date)}</span>}
                    {by && (
                      <>
                        <span>·</span>
                        <Avatar
                          src={creator?.profileImage || creator?.photoURL || creator?.profilePicture}
                          name={by}
                          size="w-5 h-5"
                        />
                        <span>{by}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-1.5 flex-wrap mt-1">
                    {report.fathom ? <Pill tone="green">🎥 Recording</Pill> : <Pill>No recording yet</Pill>}
                    {report.fathom?.actionItemCount > 0 && (
                      <Pill tone="blue">✅ {report.fathom.actionItemCount} action items</Pill>
                    )}
                    {!isV2(report) && <Pill tone="amber">Legacy format</Pill>}
                  </div>
                </button>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={pageBtn(currentPage === 1)}
              >
                Previous
              </button>
              <span className="inline-flex h-9 items-center rounded-full bg-[#F80707] px-4 text-sm font-medium text-white tabular-nums">
                Page {Math.min(currentPage, totalPages)} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className={pageBtn(currentPage >= totalPages)}
              >
                Next
              </button>
            </div>
          )}
        </GlassCard>
        </PinnableCard>
      )}

      {/* Report modal — kept outside the glass cards: backdrop-filter would trap a fixed overlay inside them */}
      {selectedReport && section && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={MODAL_PANEL}>
            <div className={`px-6 py-4 border-b ${MODAL_DIVIDER} flex items-center justify-center relative`}>
              <div className="text-center">
                <h2 className="text-xl font-medium text-gray-900 dark:text-white">
                  Meeting Report — {selectedDate ? fmtLongDate(selectedDate) : 'Unknown date'}
                </h2>
                <div className={`mt-1 flex items-center justify-center gap-2 flex-wrap text-xs ${MUTED}`}>
                  {selectedDate && <span>Saved {fmtTime(selectedDate)}</span>}
                  {creatorName(selectedReport) && <span>· by {creatorName(selectedReport)}</span>}
                  {selectedReport.fathom ? (
                    <Pill tone="green">🎥 Recording linked</Pill>
                  ) : (
                    <Pill>No recording yet</Pill>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="absolute right-4 rounded-full p-1 text-gray-400 transition hover:text-[#F80707] dark:text-white/40 dark:hover:text-[#FF4D40]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Section tabs + prev/next */}
            <div className={`px-6 py-3 border-b ${MODAL_DIVIDER} flex items-center gap-3`}>
              <GlassCircleButton
                onClick={() => setCurrentSection((p) => (p > 0 ? p - 1 : sections.length - 1))}
                title="Previous section"
              >
                <ChevronLeft className="h-5 w-5" />
              </GlassCircleButton>
              <div className="flex-1 flex items-center justify-center overflow-x-auto">
                <SlidingPills
                  size="sm"
                  options={sectionOptions}
                  value={currentSection}
                  onChange={setCurrentSection}
                  ariaLabel="Report sections"
                />
              </div>
              <GlassCircleButton
                onClick={() => setCurrentSection((p) => (p < sections.length - 1 ? p + 1 : 0))}
                title="Next section"
              >
                <ChevronRight className="h-5 w-5" />
              </GlassCircleButton>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex flex-col items-center mb-6">
                <span className="text-3xl leading-none" aria-hidden="true">
                  {section.icon}
                </span>
                <h2 className="text-xl font-medium text-gray-900 dark:text-white mt-2 text-center">{section.title}</h2>
                {!isV2(selectedReport) && section.key === 'overview' && (
                  <p className={`mt-1 text-xs text-center ${MUTED}`}>
                    Saved by the previous Meeting Mode — shown in the legacy layout.
                  </p>
                )}
              </div>

              {section.key === 'overview' && <OverviewSection report={selectedReport} reportDate={selectedDate} />}
              {section.key === 'growth' && <GrowthSection growth={selectedReport.growth} />}
              {section.key === 'clients' && <ClientsSection report={selectedReport} />}
              {section.key === 'tasks' && <TasksSection report={selectedReport} reportDate={selectedDate} />}
              {section.key === 'additional' && <AdditionalSection report={selectedReport} />}
              {section.key === 'recording' && (
                <RecordingSection
                  report={selectedReport}
                  reportDate={selectedDate}
                  summary={summary}
                  summaryLoading={summaryLoading}
                  autoLinking={autoLinking}
                  internalUsers={internalUsers}
                  recordingTasks={recordingTasks}
                  itemKeys={itemKeys}
                  assigneeById={assigneeById}
                  onPickAssignee={(idx, uid) => setAssigneeById((prev) => ({ ...prev, [idx]: uid }))}
                  creating={creating}
                  onCreateTask={handleCreateTask}
                  onCreateAll={handleCreateAll}
                  onOpenPicker={() => setPickerOpen(true)}
                  onUnlink={handleUnlink}
                  onRetryAutoLink={handleRetryAutoLink}
                />
              )}
            </div>

            <div className={`px-6 py-4 border-t ${MODAL_DIVIDER} flex justify-between`}>
              <GlassAccentButton onClick={handleDeleteMeeting}>
                <Trash2 className="h-4 w-4" />
                Delete
              </GlassAccentButton>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className={GLASS_SECONDARY_BUTTON}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <RecordingPicker
        open={pickerOpen && !!selectedReport}
        onClose={() => setPickerOpen(false)}
        reportDate={selectedDate}
        internalUsers={internalUsers}
        onLink={handleManualLink}
        excludeSummaryId={linkedSummaryId}
      />
    </GlassPage>
  );
};

export default MeetingsReports;
